import { createElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import {
  hasBasis,
  initialBasisMode,
  mapAiResult,
  type OpenCostBasisMode,
} from '@/utils/approval/openConstructionCost';
import { BasisToggleButton, JudgeArrow, Scenarios, StatusTag } from './parts';
import { OpenConstructionCostRoot } from './styles';

type OpenConstructionCostSnapshotProps = {
  /** 저장 시점 리포트 HTML(attributes.content) — 결재된 내용 그대로 */
  content: string;
  /** 판정기준 전환에만 쓰는 분석 결과 원문(attributes.data) */
  data: unknown[];
};

const isHidden = (props: Record<string, unknown>) =>
  (props.style as { display?: string } | undefined)?.display === 'none';

/**
 * 233 조회 — 레거시 appr_view_forms_open_construction_cost 와 같이 저장 HTML 을 그대로 그리고,
 * 판정기준(영업이익 ↔ 직접이익)으로 바뀌는 칸만 data 로 다시 채움(레거시 applyBasis)
 */
const OpenConstructionCostSnapshot = ({
  content,
  data,
}: OpenConstructionCostSnapshotProps) => {
  const { t, i18n } = useTranslation();
  // 판정기준 칸 문구는 보는 사람 언어 — 언어를 바꾸면 다시 만듦
  const view = useMemo(() => mapAiResult(data), [data, i18n.language]);
  const [mode, setMode] = useState<OpenCostBasisMode | null>(null);
  const basisMode = mode ?? (view ? initialBasisMode(view) : null);
  const canToggle =
    !!view && hasBasis(view.basis.operating) && hasBasis(view.basis.direct);
  const basis = view && basisMode ? view.basis[basisMode] : null;
  const active = hasBasis(basis) ? basis : null;

  const toggle = useCallback(
    () => setMode(basisMode === 'operating' ? 'direct' : 'operating'),
    [basisMode],
  );

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      const key = props.key as string;
      if (element.id === 'btn_basis_toggle') {
        return (
          <BasisToggleButton
            key={key}
            basisMode={basisMode}
            hidden={view ? !canToggle : isHidden(props)}
            onClick={canToggle ? toggle : undefined}
          />
        );
      }
      if (element.classList.contains('arrow')) {
        const tag = element.parentElement;
        if (tag?.classList.contains('judge-tag')) {
          return (
            <JudgeArrow key={key} up={tag.classList.contains('is-caution')} />
          );
        }
      }
      if (!active) return undefined;
      const text = (value: string) => createElement(tagName, props, value);
      switch (element.id) {
        case 'value_overall_opinion':
          return active.opinionHTML
            ? createElement(tagName, {
                ...props,
                dangerouslySetInnerHTML: { __html: active.opinionHTML },
              })
            : undefined;
        case 'value_roi_status':
          return active.status ? (
            <StatusTag
              key={key}
              id={element.id}
              status={active.status}
              label={t('회수 속도')}
            />
          ) : undefined;
        case 'value_roi_period_grade':
          return active.status ? (
            <StatusTag
              key={key}
              id={element.id}
              status={active.status}
              hidden={isHidden(props)}
            />
          ) : undefined;
        case 'value_roi_rate_status':
          return (
            <StatusTag
              key={key}
              id={element.id}
              status={active.rateStatus}
              label={t('이익률')}
              hidden={!active.rateStatus}
            />
          );
        case 'value_roi_rate_grade':
          return (
            <StatusTag
              key={key}
              id={element.id}
              status={active.rateStatus}
              hidden={!active.rateStatus}
            />
          );
        case 'value_roi_period':
          return text(active.period || '-');
        case 'value_roi_subcaption':
          return text(active.subcaption || '');
        case 'label_roi_profit':
          return text(active.profitLabel);
        case 'value_roi_profit':
          return text(active.monthlyProfit || '-');
        case 'value_roi_rate':
          return text(active.profitRate || '-');
        case 'value_roi_scenario':
          return active.scenarios.length ? (
            <Scenarios key={key} scenarios={active.scenarios} />
          ) : undefined;
        default:
          return undefined;
      }
    },
    [active, basisMode, canToggle, t, toggle, view],
  );

  const rendered = useHtmlToReact(content, { transform });

  return (
    <OpenConstructionCostRoot className="open-construction-cost">
      {rendered}
    </OpenConstructionCostRoot>
  );
};

export default OpenConstructionCostSnapshot;
